import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'

/**
 * `useState`-shaped hook that persists its value in `localStorage` under
 * `key`. The stored payload is always a JSON-encoded `{ v: T }` envelope so
 * we can extend it in the future without breaking previously-stored values
 * (e.g. for migrations) — when reading, anything that doesn't match the
 * envelope shape is treated as a stale/unknown value and `initialValue` is
 * used instead.
 *
 * The validator is intentionally required: `localStorage` is a free-for-all
 * shared between every tab on the same origin, so we never want to trust the
 * stored value blindly. Pass an `(input): input is T` predicate that narrows
 * arbitrary input down to `T`. Invalid stored values fall back to
 * `initialValue` and the bad entry is removed.
 *
 * Writes use `JSON.stringify` and silently swallow quota / serialization
 * errors so a full disk doesn't crash the UI.
 */
export const usePersistedState = <T>(key: string, initialValue: T, isValid: (input: unknown) => input is T): [T, Dispatch<SetStateAction<T>>] => {
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return initialValue
    try {
      const raw = window.localStorage.getItem(key)
      if (raw == null) return initialValue
      const parsed: unknown = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && 'v' in parsed && isValid((parsed as { v: unknown }).v)) {
        return (parsed as { v: T }).v
      }
      window.localStorage.removeItem(key)
      return initialValue
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, JSON.stringify({ v: state }))
    } catch {
      // Quota exceeded / private mode — silently ignore; the in-memory state
      // remains correct for this session.
    }
  }, [key, state])

  const setStateStable = useCallback<Dispatch<SetStateAction<T>>>((next) => setState(next), [])

  return [state, setStateStable]
}
